import dayjs from "dayjs";
import * as dfns from "date-fns";

/**
 * Date 형식 날짜를 'yyyy-MM-dd' 문자열로 변환하는 함수
 * @param Date ex)2025-11-28T06:31:44.820Z
 * @return 'yyyyMMdd'문자열 ex)"20251128"
 */
export const dateToString = (date: Date) => {
  const year = date.getFullYear(); // 2023
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // 06
  const day = date.getDate().toString().padStart(2, "0"); // 18
  return year + month + day;
};

/**
 * 'yyyyMMdd' 문자열을 Date 형식으로 변환하는 함수
 * @param 'yyyyMMdd'문자열 ex)"20251128"
 * @return Date ex)2025-11-28T06:31:44.820Z
 */
export const stringToDate = (date: string) => {
  return new Date(
    date.substring(0, 4) +
      "-" +
      date.substring(4, 6) +
      "-" +
      date.substring(6, 8)
  );
};

/**
 * db의 날짜코드를 'yyyyMMdd' 문자열로 변환하는 함수
 * @param db에 입력된 날짜코드 ex)0/0/0(년/월/일)
 * @return 'yyyyMMdd'문자열 ex)"20251128"
 */
export const getConvertedDate = (code?: string) => {
  if (!code) return null; // code가 없으면 null 반환

  // '/'가 포함되어 있는지 확인
  if (code.includes("/")) {
    // 현재 날짜를 기준으로 작업
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0 = January
    const currentDate = now.getDate();

    // 구분자(/)로 값을 분리
    const [yearPart, monthPart, datePart] = code.split("/");

    // 년도 계산
    const year =
      yearPart === "0" ? currentYear : currentYear + parseInt(yearPart);

    // 월 계산
    let month;
    if (monthPart === "0") {
      month = currentMonth;
    } else if (monthPart === "F" || monthPart === "f") {
      month = 0; // 1월
    } else if (monthPart === "L" || monthPart === "l") {
      month = 11; // 12월
    } else {
      month = currentMonth + parseInt(monthPart);
    }

    // 일 계산
    let date;
    if (datePart === "0") {
      date = currentDate;
    } else if (datePart === "F" || datePart === "f") {
      date = 1; // 첫째 날
    } else if (datePart === "L" || datePart === "l") {
      // 마지막 날 계산 (다음 달 1일에서 하루 빼기)
      const tempDate = new Date(year, month + 1, 0);
      date = tempDate.getDate();
    } else {
      date = currentDate + parseInt(datePart);
    }

    // 최종 날짜에서 년-월-일 추출해서 반환
    return dayjs(new Date(year, month, date)).format("YYYYMMDD");
  }

  // '/'가 없는 경우: 그대로 반환(예: 20250103)
  return code;
};

/**
 * 날짜 파싱 함수
 * @param 'yyyyMMdd'문자열 ex)"20251128"
 * @return Date ex)"2025-11-28T00:00:00.000Z"
 */
export const parseDate = (val: string): Date | null => {
  if (!val) return null;

  // YYYYMMDD → YYYY-MM-DD로 포맷 변경
  if (/^\d{8}$/.test(val)) {
    val = `${val.substring(0, 4)}-${val.substring(4, 6)}-${val.substring(
      6,
      8
    )}`;
  }

  return new Date(val);
};

/**
 * @return
 */
export const getCurrentTime = () => {
  const now = new Date();
  const formattedTime =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getDate()).padStart(2, "0") +
    " " +
    String(now.getHours()).padStart(2, "0") +
    ":" +
    String(now.getMinutes()).padStart(2, "0") +
    ":" +
    String(now.getSeconds()).padStart(2, "0") +
    "." +
    String(now.getMilliseconds()).padStart(3, "0");

  return formattedTime;
};

/**
 *
 * @param fmt
 * @return
 */
const normalizeDateFnsFormat = (fmt?: string): string => {
  if (!fmt || fmt.trim() === "") return "yyyyMMdd";
  const raw = fmt.trim();

  const shortcuts: Record<string, string> = {
    yyyymmdd: "yyyyMMdd",
    "yyyy-mm-dd": "yyyy-MM-dd",
    "yyyy/mm/dd": "yyyy/MM/dd",
    "mm-dd-yy": "MM-dd-yy",
    "dd-mm-yy": "dd-MM-yy",
    "mm/dd/yy": "MM/dd/yy",
    "dd/mm/yy": "dd/MM/yy",
    "mm-dd-yyyy": "MM-dd-yyyy",
    "dd-mm-yyyy": "dd-MM-yyyy",
    "mm/dd/yyyy": "MM/dd/yyyy",
    "dd/mm/yyyy": "dd/MM/yyyy",
  };

  const key = raw.toLowerCase();
  if (shortcuts[key]) return shortcuts[key];

  // 일반 규칙 변환
  return raw
    .replace(/mm/g, "MM")
    .replace(/m/g, "M")
    .replace(/y/gi, "y")
    .replace(/dd/gi, "dd");
};

/**
 * 설정된 날짜형식에 맞는지 체크
 * @param value
 * @param fmt
 */
export const isDateByFormat = (value: string, fmt?: string): boolean => {
  if (typeof value !== "string" || value.trim() === "") return false;

  const pattern = normalizeDateFnsFormat(fmt);
  const parsed = dfns.parse(value, pattern, new Date());

  // parse → isValid → format(parsed) 결과가 원본과 같아야만 true
  return dfns.isValid(parsed) && dfns.format(parsed, pattern) === value;
};

/**
 * 모든 날짜 형식을  yyyyMMdd 변환
 * @param value
 * @param fmt
 */
export const parseToyyyyMMdd = (value: string, fmt?: string): string | null => {
  if (typeof value !== "string" || value.trim() === "") return null;

  const pattern = normalizeDateFnsFormat(fmt);
  const parsed = dfns.parse(value, pattern, new Date());

  if (!dfns.isValid(parsed) || dfns.format(parsed, pattern) !== value) {
    return null; // 유효하지 않음
  }

  // 항상 yyyyMMdd로 반환
  return dfns.format(parsed, "yyyyMMdd");
};
/**
 * 날짜 파싱 함수
 * @param 'yyyyMMdd'문자열 ex)"20260219" , 'yyyyMMdd , '-'' ex) "20260219, '-'"
 * @param separator "/", ".", "-"
 * @return 'yyyy/MM/dd' 문자열 ex)"2026/02/19" , 'yyyy-MM-dd' ex) "2026-02-19"
 */
export const formatYyyyMMdd = (value: string, separator: string = "/") => {
  if (!/^\d{8}$/.test(value)) return value;
  return `${value.slice(0, 4)}${separator}${value.slice(4, 6)}${separator}${value.slice(6, 8)}`;
};

/**
 * Date 형식을 'yyyy-MM-dd HH:mm:ss' 문자열로 변환
 * @param date Date
 * @return 문자열 ex) "2026-05-15 14:30:22"
 */
export const dateTimeToString = (date: Date) => {
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss.SSS");
};
